/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import OfferCard from "../components/OfferCard";
import { ECOSYSTEM_TOPICS } from "../config/ecosystemTopics";
import BRAND from "../constants/brand";
import { Sparkles, Filter, Lock, ArrowRight } from "lucide-react";

/**
 * Phase 6.4 — Business Offers landing page.
 *
 * Replaces the editorial /business/offers stub with the real Member Benefits
 * surface: filters by category, member-gated reveal, and the editorial
 * "What we cover / How it works" copy from the ecosystem config below.
 */
export default function BusinessOffersPage() {
  const { t } = useTranslation();
  const topic = ECOSYSTEM_TOPICS["business/offers"] || {};
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    setLoading(true);
    API.get("/business/offers?limit=48")
      .then(({ data }) => setOffers(data.items || []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, []);

  // Derive the categories present on the loaded offers
  const categories = useMemo(() => {
    const set = new Set();
    offers.forEach((o) => (o.category_tags || []).forEach((c) => set.add(c)));
    return ["all", ...Array.from(set)];
  }, [offers]);

  const visibleOffers = useMemo(() => {
    if (activeCategory === "all") return offers;
    return offers.filter((o) => (o.category_tags || []).includes(activeCategory));
  }, [offers, activeCategory]);

  return (
    <div data-testid="page-business-offers">
      {/* ============================== HERO ============================== */}
      <section className="relative overflow-hidden border-b border-white/10">
        {topic.image && (
          <div className="absolute inset-0">
            <img src={topic.image} alt="" className="w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/25" />
          </div>
        )}
        <div className="relative app-container py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">{t("offers.hero_eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-[20ch]" data-testid="offers-title">
              {t("offers.page_title", "Member Benefits")}
            </h1>
            <p className="text-white/82 mt-5 leading-relaxed max-w-[60ch] body-copy text-lg">
              {t(
                "offers.page_subtitle",
                "Exclusive offers from partner businesses across the DONiTSH!T ecosystem — therapy intros, retreat discounts, program bundles, and more. Sign in (free) to reveal the codes."
              )}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#offers-grid" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="offers-view-cta">
                <Sparkles className="w-4 h-4" /> {t("offers.view_offers", "View offers")}
              </a>
              <Link to="/marketplace" className="btn-ghost text-sm inline-flex items-center gap-2">
                {t("offers.explore_marketplace", "Explore Marketplace")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== FILTERS ============================== */}
      <Section
        eyebrow={t("offers.filter_eyebrow", "— Filter")}
        title={t("offers.filter_title", "Find an offer that fits")}
        subtitle={t(
          "offers.filter_subtitle",
          "Browse by category. New offers are rotated in regularly."
        )}
      >
        <div id="offers-grid" />
        <div className="flex flex-wrap gap-2 mb-7" data-testid="offers-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              data-testid={`offers-filter-${c}`}
              className={`text-xs px-3.5 py-2 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                activeCategory === c
                  ? "bg-gradient-to-r from-[#E8D199] via-[#D4AF37] to-[#7A5F2A] border-transparent text-black"
                  : "border-white/15 text-white/75 hover:border-[#C8A24A]/45 hover:text-white"
              }`}
            >
              <Filter className="w-3 h-3" />
              {c === "all" ? t("offers.all", "All") : prettyCategory(c)}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="offers-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card-surface aspect-[16/12] animate-pulse" />
            ))}
          </div>
        ) : visibleOffers.length === 0 ? (
          <EmptyOffers />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="offers-grid">
            {visibleOffers.map((o) => (
              <OfferCard key={o.offer_id} offer={o} returnUrl="/business/offers" />
            ))}
          </div>
        )}
      </Section>

      {/* ============================== PILLARS (Editorial) ============================== */}
      {topic.pillars && topic.pillars.length > 0 && (
        <Section
          eyebrow={t("offers.editorial_eyebrow", "— What we cover")}
          title={t("offers.editorial_title", "How offers work")}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="offers-editorial">
            {topic.pillars.map((p, i) => (
              <div key={i} className="card-surface p-5">
                <div className="font-display text-lg mb-2">{p.title}</div>
                <p className="text-sm text-white/72 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ============================== ASK DONNY ============================== */}
      <Section eyebrow={t("offers.donny_eyebrow", "— Concierge")} title={t("offers.donny_title", "Ask Donny about offers")}>
        <div className="card-surface p-6 md:p-8 grid md:grid-cols-[140px_1fr_auto] items-center gap-6" data-testid="offers-donny-block">
          <img
            src={BRAND.DONNY_AVATAR}
            alt="Donny"
            className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover ring-1 ring-[#C8A24A]/35 shadow-[0_0_40px_rgba(212,175,55,0.25)] mx-auto md:mx-0"
          />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> {t("offers.donny_meta_chip")}
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight">
              {t("offers.donny_hl")}
            </h3>
            <p className="text-white/72 mt-2 max-w-[60ch] leading-relaxed body-copy">
              {t("offers.donny_sub")}
            </p>
          </div>
          <Link
            to="/ask-donny"
            className="btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto"
            data-testid="offers-donny-cta"
          >
            <Sparkles className="w-4 h-4" /> {t("nav.ask_donny")}
          </Link>
        </div>
      </Section>
    </div>
  );
}

function EmptyOffers() {
  const { t } = useTranslation();
  return (
    <div className="card-surface p-10 text-center" data-testid="offers-empty">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E2C77A]/30 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center mx-auto mb-4">
        <Lock className="w-5 h-5 gold-text" />
      </div>
      <div className="font-display text-xl">{t("offers.empty_title", "No offers in this category yet")}</div>
      <p className="text-sm text-white/65 mt-2 max-w-md mx-auto">
        {t(
          "offers.empty_body",
          "We rotate a curated set of offers each month. Check back soon, or browse all categories."
        )}
      </p>
    </div>
  );
}

function prettyCategory(slug) {
  return String(slug || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
